import AbstractCanvasTransformTool from "ui/canvas/transforms/AbstractCanvasTransformTool";
import Utils from "ui/helpers/Utils";
import {SCENE_FRAMES, STATIC_URL} from "ui/config";

const SCALE_FRAME_PERIOD = SCENE_FRAMES >> 2;
const SCALES = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
const SCALE_FRAMES = [0, 2, 1, 3];

/**
 * Transform layer: makes displacement like water waves
 */
export default class CanvasWaterWaves extends AbstractCanvasTransformTool {
	constructor() {
		super();

		/**
		 * @type {HTMLImageElement|null}
		 * @private
		 */
		this._wavesImg = null;

		/**
		 * @type {HTMLCanvasElement|null}
		 * @private
		 */
		this._wavesCanvas = null;
	}

	/**
	 * @inheritDoc
	 */
	_isNeedKeyframesForSelection(fromFrame, toFrame) {
		return fromFrame !== toFrame;
	}

	/**
	 * @param {CanvasLayer} layer
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {void}
	 * @protected
	 */
	async _prepare(layer, fromFrame, toFrame) {
		if (this._wavesImg !== null) {
			return;
		}

		this._wavesImg = await Utils.loadImage(STATIC_URL + '/images/waves.jpg');
		this._wavesCanvas = document.createElement('canvas');
		this._wavesCanvas.width = this._wavesImg.naturalWidth;
		this._wavesCanvas.height = this._wavesImg.naturalHeight;
	}

	/**
	 * @returns {void}
	 * @protected
	 */
	async _clearMemory() {
		this._wavesImg = null;
		this._wavesCanvas = null;
	}

	/**
	 * @param {HTMLCanvasElement} canvas
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {HTMLImageElement} img
	 * @param {number} frameIndex
	 * @returns {?T_DataSetItem}
	 * @protected
	 */
	_processImage(canvas, ctx, img, frameIndex) {
		this._mixWaves(frameIndex);
		this._makeDisplacement(canvas, img);
		return this._dataSet.setData(canvas.toDataURL('image/png'));
	}

	/**
	 * @param {number} frameIndex
	 * @private
	 */
	_mixWaves(frameIndex) {
		const frameA = Math.floor((frameIndex % SCENE_FRAMES) / SCALE_FRAME_PERIOD);
		const alpha = (frameIndex % SCALE_FRAME_PERIOD) / SCALE_FRAME_PERIOD;
		const frameB = (frameA + 1) % 4;
		let shiftX = ((SCALE_FRAME_PERIOD + frameIndex) << 1) % this._wavesCanvas.width;

		const ctx = this._wavesCanvas.getContext('2d');
		ctx.save();
		let [scaleX, scaleY] = SCALES[SCALE_FRAMES[frameA]];
		ctx.scale(scaleX, scaleY);
		ctx.translate(
			scaleX === -1 ? -this._wavesCanvas.width : 0,
			scaleY === -1 ? -this._wavesCanvas.height : 0
		);
		ctx.drawImage(this._wavesImg, scaleX * shiftX, 0);
		ctx.drawImage(this._wavesImg, scaleX * (-this._wavesCanvas.width + shiftX), 0);
		ctx.restore();

		if (frameB === 0) {
			shiftX = ((frameIndex % SCALE_FRAME_PERIOD) << 1) % this._wavesCanvas.width;
		}

		ctx.save();
		ctx.globalAlpha = alpha;
		[scaleX, scaleY] = SCALES[SCALE_FRAMES[frameB]];
		ctx.scale(scaleX, scaleY);
		ctx.translate(
			scaleX === -1 ? -this._wavesCanvas.width : 0,
			scaleY === -1 ? -this._wavesCanvas.height : 0
		);
		ctx.drawImage(this._wavesImg, scaleX * shiftX, 0);
		ctx.drawImage(this._wavesImg, scaleX * (-this._wavesCanvas.width + shiftX), 0);
		ctx.restore();
	}

	_makeDisplacement(workCanvas, img) {
		const workCtx = workCanvas.getContext('2d');
		workCtx.clearRect(0, 0, workCanvas.width, workCanvas.height);
		const workImageData = workCtx.getImageData(0, 0, workCanvas.width, workCanvas.height);
		const workPixels = workImageData.data;

		workCtx.drawImage(img, 0, 0);
		const bgPixels = workCtx.getImageData(0, 0, workCanvas.width, workCanvas.height).data;

		const wavesPixels = this._wavesCanvas.getContext('2d')
			.getImageData(0, 0, this._wavesCanvas.width, this._wavesCanvas.height).data;

		const wavesHeight = this._wavesCanvas.height;
		const wavesWidth = this._wavesCanvas.width;
		const wavesPixelsCount = wavesPixels.length >> 2;
		const maxShiftDelta = 2;
		const maxLightDelta = 0x20;
		const fullWidth = workCanvas.width;
		const fullHeight = workCanvas.height;
		let fromX = 0;
		let toX = fullWidth;
		let fromY = 0;
		let toY = fullHeight;

		if (this._sel.hasSelection && this._sel.isRectangularSelection) {
			const sel = this._sel.selectedAreaRect;
			fromX = sel.x;
			fromY = sel.y;
			toX = sel.x + sel.width;
			toY = sel.y + sel.height;
		}

		for (let y = fromY; y < toY; y++) {
			for (let x = fromX; x < toX; x++) {
				const index = (y * fullWidth + x) << 2;
				const wavesX = x % wavesWidth;
				const wavesY = y % wavesHeight;
				const wavesIndex = wavesY * wavesWidth + wavesX;
				const wavesIndexInv = wavesPixelsCount - wavesIndex;
				const levelY = wavesPixels[wavesIndex << 2] / 256;
				const levelX = wavesPixels[wavesIndexInv << 2] / 256;
				const shiftY = (maxShiftDelta * levelY) >> 0;
				const shiftX = (maxShiftDelta * levelX) >> 0;
				const shiftLight = (maxLightDelta * Math.min(levelY, levelX)) >> 0;

				let newY = y - shiftY;
				if (newY < 0) {
					newY = 0;
				} else if (newY > toY - 1) {
					newY = toY - 1;
				}

				let newX = x - shiftX;
				if (newX < 0) {
					newX = 0;
				} else if (newX > toX - 1) {
					newX = toX - 1;
				}

				const shiftedIndex = (newY * fullWidth + newX) << 2;
				workPixels[index] = Math.min(0xFF, bgPixels[shiftedIndex] + shiftLight);
				workPixels[index + 1] = Math.min(0xFF, bgPixels[shiftedIndex + 1] + shiftLight);
				workPixels[index + 2] = Math.min(0xFF, bgPixels[shiftedIndex + 2] + shiftLight);
				workPixels[index + 3] = bgPixels[shiftedIndex + 3];
			}
		}

		workCtx.putImageData(workImageData, 0, 0);
	}

}
